import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, X, Send, Loader2, ArrowRight, Phone } from "lucide-react";

const SUGGESTIONS = ["Find an HVAC company near Springfield", "Best coffee shops nearby", "How does the postcard mailer work?"];

export default function AiAssistant() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ id: "welcome", role: "assistant", text: "Hi! I'm your Saving America Money helper. Ask me to find a local business or explain the postcard mailer." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(null);
  const [cbOpen, setCbOpen] = useState(false);
  const [cb, setCb] = useState({ name: "", phone: "" });
  const [cbSent, setCbSent] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: q, session_id: sid });
      setSid(data.session_id);
      setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "assistant", text: data.reply, browseUrl: data.browse_url, businesses: data.businesses || [], filterLabel: data.filter_label, nearby: data.nearby || [] }]);
    } catch {
      setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "assistant", text: "Sorry, I had trouble responding. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const requestCallback = async () => {
    if (!cb.phone.trim()) return;
    try {
      await api.post("/ai/lead", { name: cb.name, phone: cb.phone, session_id: sid, note: "Callback requested from website chat." });
      setCbSent(true); setCbOpen(false);
      setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "assistant", text: "Thanks! Our team has your number and will reach out shortly." }]);
    } catch {
      setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "assistant", text: "Sorry, we couldn't submit that just now. Please try again." }]);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="ai-assistant-toggle"
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-sam-crimson text-white shadow-2xl grid place-items-center hover:scale-105 transition-transform">
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm rounded-2xl border border-sam-navy/15 bg-white shadow-2xl overflow-hidden flex flex-col" style={{ height: "min(560px, 75vh)" }} data-testid="ai-assistant-panel">
          <div className="sam-flag-bar h-1 w-full" />
          <div className="bg-sam-navy text-sam-cream px-4 py-3 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-sam-amber" />
            <div><div className="font-display font-bold leading-none">SAM Assistant</div><div className="text-[11px] text-sam-cream/60">Powered by Claude</div></div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-sam-cream/40">
            {msgs.map((m, i) => (
              <div key={m.id ?? i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-sam-navy text-white rounded-br-sm" : "bg-white border border-sam-navy/10 text-sam-navy rounded-bl-sm"}`} data-testid={`ai-msg-${m.role}`}>
                  {m.text}
                </div>
                {m.role === "assistant" && (m.businesses?.length > 0) && (
                  <div className="mt-2 w-full max-w-[88%] flex flex-col gap-1.5">
                    {m.businesses.map((b) => (
                      <Link key={b.slug} to={`/business/${b.slug}?from=assistant`} onClick={() => setOpen(false)} data-testid={`ai-rec-${b.slug}`}
                        className="flex items-center justify-between gap-2 rounded-lg border border-sam-navy/10 bg-white px-3 py-2 text-xs hover:border-sam-crimson/60 hover:bg-sam-crimson/5 transition-colors">
                        <span className="font-semibold text-sam-navy truncate">{b.name}</span>
                        <span className="text-[11px] text-sam-slate shrink-0 inline-flex items-center gap-1">{b.city}, {b.state_abbr}<ArrowRight className="h-3 w-3" /></span>
                      </Link>
                    ))}
                  </div>
                )}
                {m.role === "assistant" && m.browseUrl && (m.businesses?.length > 0) && (
                  <div className="mt-2 flex flex-col items-start gap-1">
                    {m.filterLabel && <span className="text-[11px] font-semibold text-sam-navy/70" data-testid="ai-filter-label">Showing {m.filterLabel}</span>}
                    <Link to={m.browseUrl} onClick={() => setOpen(false)} data-testid="ai-view-businesses"
                      className="inline-flex items-center gap-1.5 rounded-full bg-sam-crimson px-3.5 py-1.5 text-xs font-bold text-white shadow hover:gap-2.5 transition-all">
                      View all in Browse <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                )}
                {m.role === "assistant" && (m.businesses?.length === 0) && (m.nearby?.length > 0) && (
                  <div className="mt-2 w-full max-w-[88%]" data-testid="ai-nearby">
                    <div className="text-[11px] font-semibold text-sam-navy/70 mb-1">Nearby markets with listings</div>
                    <div className="flex flex-col gap-1.5">
                      {m.nearby.map((n) => (
                        <Link key={`${n.state_abbr}-${n.slug}`} to={`/city/${n.slug}?state=${n.state_abbr}`} onClick={() => setOpen(false)}
                          data-testid={`ai-nearby-${n.slug}`}
                          className="flex items-center justify-between gap-2 rounded-lg border border-sam-navy/10 bg-white px-3 py-2 text-xs hover:border-sam-crimson/60 hover:bg-sam-crimson/5 transition-colors">
                          <span className="font-semibold text-sam-navy truncate">{n.name}, {n.state_abbr}</span>
                          <span className="text-[11px] text-sam-slate shrink-0 inline-flex items-center gap-1">{n.count} · {n.distance_mi} mi<ArrowRight className="h-3 w-3" /></span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-white border border-sam-navy/10 rounded-2xl px-3.5 py-2"><Loader2 className="h-4 w-4 animate-spin text-sam-crimson" /></div></div>}
            {msgs.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="text-xs px-2.5 py-1 rounded-full border border-sam-crimson/40 text-sam-crimson hover:bg-sam-crimson/5">{s}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>
          {!cbSent && (
            <div className="px-3 pt-2 border-t border-sam-navy/10">
              {cbOpen ? (
                <div className="flex flex-col gap-2 pb-1" data-testid="ai-callback-form">
                  <div className="flex gap-2">
                    <Input value={cb.name} onChange={(e) => setCb({ ...cb, name: e.target.value })} placeholder="Your name" data-testid="ai-cb-name" />
                    <Input value={cb.phone} onChange={(e) => setCb({ ...cb, phone: e.target.value })} placeholder="Phone" data-testid="ai-cb-phone" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-sam-navy hover:bg-sam-navy/90 flex-1" onClick={requestCallback} disabled={!cb.phone.trim()} data-testid="ai-cb-submit">Request callback</Button>
                    <Button size="sm" variant="ghost" onClick={() => setCbOpen(false)} data-testid="ai-cb-cancel">Cancel</Button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setCbOpen(true)} data-testid="ai-callback-toggle" className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-sam-crimson py-1.5 hover:bg-sam-crimson/5 rounded-md">
                  <Phone className="h-3.5 w-3.5" /> Get a callback from our team
                </button>
              )}
            </div>
          )}
          <div className="p-3 border-t border-sam-navy/10 flex items-center gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about local businesses…" data-testid="ai-input" />
            <Button size="icon" className="bg-sam-crimson hover:bg-sam-crimson/90 shrink-0" onClick={() => send()} disabled={busy} data-testid="ai-send"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </>
  );
}
